/**
 * 4-component vector used for entity colours and per-instance shader
 * attributes. Follows the same `dest?` mutate-or-return convention as
 * {@link vec2} (write to `dest` if provided, else mutate `this`).
 *
 * The instance stores its components in a length-4 `values` array; the
 * `x`/`y`/`z`/`w` and `r`/`g`/`b`/`a` accessors are aliases on the same
 * storage. `set xyzw([...])` and the rgba setters are the canonical
 * bulk-write paths.
 *
 * Unlike `vec2`, the static helpers (`sum`, `difference`, `product`,
 * `mix`, etc.) and the per-instance `add` / `subtract` / `multiply` /
 * `divide` methods do **not** all take `dest?` — some mutate `this`
 * unconditionally. Check the call site before relying on aliasing.
 */
import { epsilon } from './constants';

export class vec4 {

  get x(): number {
    return this.values[0];
  }

  get y(): number {
    return this.values[1];
  }

  get z(): number {
    return this.values[2];
  }

  get w(): number {
    return this.values[3];
  }

  get xy(): [number, number] {
    return [
      this.values[0],
      this.values[1]
    ];
  }

  get xyz(): [number, number, number] {
    return [
      this.values[0],
      this.values[1],
      this.values[2]
    ];
  }

  get xyzw(): [number, number, number, number] {
    return [
      this.values[0],
      this.values[1],
      this.values[2],
      this.values[3]
    ];
  }

  set x(value: number) {
    this.values[0] = value;
  }

  set y(value: number) {
    this.values[1] = value;
  }

  set z(value: number) {
    this.values[2] = value;
  }

  set w(value: number) {
    this.values[3] = value;
  }

  set xy(values: [number, number]) {
    this.values[0] = values[0];
    this.values[1] = values[1];
  }

  set xyz(values: [number, number, number]) {
    this.values[0] = values[0];
    this.values[1] = values[1];
    this.values[2] = values[2];
  }

  set xyzw(values: [number, number, number, number]) {
    this.values[0] = values[0];
    this.values[1] = values[1];
    this.values[2] = values[2];
    this.values[3] = values[3];
  }

  get r(): number {
    return this.values[0];
  }

  get g(): number {
    return this.values[1];
  }

  get b(): number {
    return this.values[2];
  }

  get a(): number {
    return this.values[3];
  }

  get rg(): [number, number] {
    return [
      this.values[0],
      this.values[1]
    ];
  }

  get rgb(): [number, number, number] {
    return [
      this.values[0],
      this.values[1],
      this.values[2]
    ];
  }

  get rgba(): [number, number, number, number] {
    return [
      this.values[0],
      this.values[1],
      this.values[2],
      this.values[3]
    ];
  }

  set r(value: number) {
    this.values[0] = value;
  }

  set g(value: number) {
    this.values[1] = value;
  }

  set b(value: number) {
    this.values[2] = value;
  }

  set a(value: number) {
    this.values[3] = value;
  }

  set rg(values: [number, number]) {
    this.values[0] = values[0];
    this.values[1] = values[1];
  }

  set rgb(values: [number, number, number]) {
    this.values[0] = values[0];
    this.values[1] = values[1];
    this.values[2] = values[2];
  }

  set rgba(values: [number, number, number, number]) {
    this.values[0] = values[0];
    this.values[1] = values[1];
    this.values[2] = values[2];
    this.values[3] = values[3];
  }

  constructor(values?: [number, number, number, number]) {
    if (values !== undefined) {
      this.xyzw = values;
    }
  }

  private values = new Float32Array(4);

  // QA-003: shared static "constants". vec4 backs its fields with a
  // Float32Array, and the JS spec forbids Object.freeze on typed-array views
  // ("Cannot freeze array buffer views with elements"). We provide freeze
  // protection via two layers:
  //   1. Object.freeze(v) locks the wrapper instance (prevents `values`
  //      reassignment, property additions, prototype swap).
  //   2. The single-component setters (x/y/z/w and r/g/b/a aliases) are
  //      shadowed on the frozen instance so the most common stray-mutation
  //      patterns (`vec4.zero.x = 5`) throw TypeError. Multi-component
  //      setters (xy, xyzw, rgba, …) remain live; covering them requires
  //      migrating off the Float32Array backing store, which is backlog.
  static readonly zero: vec4 = vec4.frozen([0, 0, 0, 1]);
  static readonly one: vec4 = vec4.frozen([1, 1, 1, 1]);

  private static frozen(values: [number, number, number, number]): vec4 {
    const v = new vec4(values);
    const captured: [number, number, number, number] = [values[0], values[1], values[2], values[3]];
    const throwOnSet = (): never => {
      throw new TypeError('Cannot mutate a frozen vec4 static');
    };
    // Shadow each single-component accessor (x/y/z/w + rgba aliases) with a
    // pair that reads the captured snapshot and rejects writes.
    const componentAccessors: Array<[string, () => number]> = [
      ['x', () => captured[0]],
      ['y', () => captured[1]],
      ['z', () => captured[2]],
      ['w', () => captured[3]],
      ['r', () => captured[0]],
      ['g', () => captured[1]],
      ['b', () => captured[2]],
      ['a', () => captured[3]],
    ];
    for (const [key, getter] of componentAccessors) {
      Object.defineProperty(v, key, {
        get: getter,
        set: throwOnSet,
        configurable: false,
        enumerable: true,
      });
    }
    return Object.freeze(v) as vec4;
  }

  at(index: number): number {
    return this.values[index];
  }

  reset(): void {
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.w = 0;
  }

  copy(dest?: vec4): vec4 {
    if (!dest) {
      dest = new vec4();
    }

    dest.x = this.x;
    dest.y = this.y;
    dest.z = this.z;
    dest.w = this.w;

    return dest;
  }

  negate(dest?: vec4): vec4 {
    if (!dest) {
      dest = this;
    }

    dest.x = -this.x;
    dest.y = -this.y;
    dest.z = -this.z;
    dest.w = -this.w;

    return dest;
  }

  equals(vector: vec4, threshold: number = epsilon): boolean {
    if (Math.abs(this.x - vector.x) > threshold) {
      return false;
    }

    if (Math.abs(this.y - vector.y) > threshold) {
      return false;
    }

    if (Math.abs(this.z - vector.z) > threshold) {
      return false;
    }

    if (Math.abs(this.w - vector.w) > threshold) {
      return false;
    }

    return true;
  }

  length(): number {
    return Math.sqrt(this.squaredLength());
  }

  squaredLength(): number {
    const x = this.x;
    const y = this.y;
    const z = this.z;
    const w = this.w;

    return (x * x + y * y + z * z + w * w);
  }

  add(vector: vec4): vec4 {
    this.x += vector.x;
    this.y += vector.y;
    this.z += vector.z;
    this.w += vector.w;

    return this;
  }

  subtract(vector: vec4): vec4 {
    this.x -= vector.x;
    this.y -= vector.y;
    this.z -= vector.z;
    this.w -= vector.w;

    return this;
  }

  multiply(vector: vec4): vec4 {
    this.x *= vector.x;
    this.y *= vector.y;
    this.z *= vector.z;
    this.w *= vector.w;

    return this;
  }

  divide(vector: vec4): vec4 {
    this.x /= vector.x;
    this.y /= vector.y;
    this.z /= vector.z;
    this.w /= vector.w;

    return this;
  }

  scale(value: number, dest?: vec4): vec4 {
    if (!dest) {
      dest = this;
    }

    dest.x *= value;
    dest.y *= value;
    dest.z *= value;
    dest.w *= value;

    return dest;
  }

  normalize(dest?: vec4): vec4 {
    if (!dest) {
      dest = this;
    }

    let length = this.length();

    if (length === 1) {
      return this;
    }

    if (length === 0) {
      dest.x *= 0;
      dest.y *= 0;
      dest.z *= 0;
      dest.w *= 0;

      return dest;
    }

    length = 1.0 / length;

    dest.x *= length;
    dest.y *= length;
    dest.z *= length;
    dest.w *= length;

    return dest;
  }

  static mix(vector: vec4, vector2: vec4, time: number, dest?: vec4): vec4 {
    if (!dest) {
      dest = new vec4();
    }

    dest.x = vector.x + time * (vector2.x - vector.x);
    dest.y = vector.y + time * (vector2.y - vector.y);
    dest.z = vector.z + time * (vector2.z - vector.z);
    dest.w = vector.w + time * (vector2.w - vector.w);

    return dest;
  }

  static sum(vector: vec4, vector2: vec4, dest?: vec4): vec4 {
    if (!dest) {
      dest = new vec4();
    }

    dest.x = vector.x + vector2.x;
    dest.y = vector.y + vector2.y;
    dest.z = vector.z + vector2.z;
    dest.w = vector.w + vector2.w;

    return dest;
  }

  static difference(vector: vec4, vector2: vec4, dest?: vec4): vec4 {
    if (!dest) {
      dest = new vec4();
    }

    dest.x = vector.x - vector2.x;
    dest.y = vector.y - vector2.y;
    dest.z = vector.z - vector2.z;
    dest.w = vector.w - vector2.w;

    return dest;
  }

  static product(vector: vec4, vector2: vec4, dest?: vec4): vec4 {
    if (!dest) {
      dest = new vec4();
    }

    dest.x = vector.x * vector2.x;
    dest.y = vector.y * vector2.y;
    dest.z = vector.z * vector2.z;
    dest.w = vector.w * vector2.w;

    return dest;
  }

  static quotient(vector: vec4, vector2: vec4, dest?: vec4): vec4 {
    if (!dest) {
      dest = new vec4();
    }

    dest.x = vector.x / vector2.x;
    dest.y = vector.y / vector2.y;
    dest.z = vector.z / vector2.z;
    dest.w = vector.w / vector2.w;

    return dest;
  }
}
